import { useContext, useEffect, useMemo, useRef, useState } from "react";
import { useDispatch } from "react-redux";

import Accessor from "@arcgis/core/core/Accessor.js";
import MapView from "@arcgis/core/views/MapView";
import AreaMeasurement2DViewModel from "@arcgis/core/widgets/AreaMeasurement2D/AreaMeasurement2DViewModel.js";
import DistanceMeasurement2DViewModel from "@arcgis/core/widgets/DistanceMeasurement2D/DistanceMeasurement2DViewModel.js";

import { setMeasureToolOpen } from "../../../store/appSlice";
import NfsButton from "../../genericComponents/NfsButton/NfsButton";
import "./MeasurementControl.css";

import * as reactiveUtils from "@arcgis/core/core/reactiveUtils.js";
import Polyline from "@arcgis/core/geometry/Polyline";
import Graphic from "@arcgis/core/Graphic";
import GraphicsLayer from "@arcgis/core/layers/GraphicsLayer";
import TextSymbol from "@arcgis/core/symbols/TextSymbol.js";
import Draw from "@arcgis/core/views/draw/Draw";
import SketchViewModel from "@arcgis/core/widgets/Sketch/SketchViewModel";
import AreaIcon from "../../../assets/measurementControl/area.svg?react";
import LineIcon from "../../../assets/measurementControl/line.svg?react";
import PeilelinjalIcon from "../../../assets/measurementControl/peilelinjal.svg?react";
import PreviousIcon from "../../../assets/measurementControl/previous.svg?react";
import ResetIcon from "../../../assets/measurementControl/reset.svg?react";
import RulerIcon from "../../../assets/ruler.svg?react";
import { getMapView } from "../../../utils/arcgisUtils";
import Checkbox from "../../genericComponents/Checkbox/Checkbox";
import FloatingWindow from "../../genericComponents/FloatingWindow/FloatingWindow";
import NfsDropdown from "../../genericComponents/NfsDropdown/NfsDropdown";
import { MeasureType, Unit } from "./measurementEnums";
import { appendMeasurement, formatLength, replaceMeasurementGraphics } from "./measurementResults";
import { drawPeilelinjal } from "./PeilelinjalGraphics";
import * as geodeticLengthOperator from "@arcgis/core/geometry/operators/geodeticLengthOperator.js";
import { AppContext } from "../../../Context";
import { buildAngleGraphics, drawAngle } from "./AngleToolGraphics";
import type { UpdateEvent } from "@arcgis/core/widgets/Sketch/types";
import type PolylineDrawAction from "@arcgis/core/views/draw/PolylineDrawAction";
import type Point from "@arcgis/core/geometry/Point";

const PEILELINJAL_ENABLED_FLAG = false;

// A finished measurement is either the graphics a draw tool produced, or a measurement view model
type PrevMeasureEntry = Array<Graphic> | { vm?: DistanceMeasurement2DViewModel | AreaMeasurement2DViewModel };

const MeasurementControl = () => {
    const dispatch = useDispatch();

    const appContext = useContext(AppContext);
    // The feature open in the object view panel can be used as the angle tool's start point
    const shownFeature = appContext?.shownFeature.value;
    const shownPoint = shownFeature?.geometry?.type === "point" ? (shownFeature.geometry as Point) : null;
    const hasShownPointFeature = shownPoint !== null;

    const measureToolRef = useRef(document.createElement("div"));

    const [prevMeasures, setPrevMeasures] = useState<Array<any>>([]);

    const measurementVMRef = useRef<DistanceMeasurement2DViewModel | AreaMeasurement2DViewModel | null>(null);

    const [distanceActive, setDistanceActive] = useState<boolean>(false);
    const [areaActive, setAreaActive] = useState<boolean>(false);
    const [peilelinjalActive, setPeilelinjalActive] = useState<boolean>(false);
    const [angleToolActive, setAngleToolActive] = useState<boolean>(false);
    const [measurementVM, setMeasurementVM] = useState<
        DistanceMeasurement2DViewModel | AreaMeasurement2DViewModel | null
    >(null);
    const [currentMeasures, setCurrentMeasures] = useState<Array<{ key: string; value: string }>>([]);
    const [unit, setUnit] = useState<string | null>(Unit.Meter);
    const [useSelectedFeatureAsStart, setUseSelectedFeatureAsStart] = useState<boolean>(true);
    // Bumped whenever a measurement view model recomputes its label, so the result list re-derives
    const [labelVersion, setLabelVersion] = useState<number>(0);
    const [peilelinjalDraw, setPeilelinjalDraw] = useState<Draw | null>(null);
    const [angleDraw, setAngleDraw] = useState<Draw | null>(null);
    // Need this ref in order to destroy the draw object when the component unmounts
    const peilelinjalDrawRef = useRef<Draw | null>(peilelinjalDraw);
    const angleDrawRef = useRef<Draw | null>(angleDraw);
    const prevMeasuresRef = useRef<Array<any>>(prevMeasures);
    const unitRef = useRef<string | null>(unit);
    const areaMeasurementVmRef = useRef<AreaMeasurement2DViewModel | null>(null);
    const activeMeasureItemRef = useRef<HTMLLIElement | null>(null);
    const angleSketchVMRef = useRef<SketchViewModel | null>(null);
    // Drag frames overlap, so each rebuild carries a sequence number and only the newest applies
    const angleEditSequenceRef = useRef<number>(0);
    const labelNormalizeIntervalRef = useRef<ReturnType<typeof setInterval> | null>(null);

    useEffect(() => {
        unitRef.current = unit;
    }, [unit]);

    useEffect(() => {
        geodeticLengthOperator.load();

        return () => {
            const mapview = getMapView();
            if (!mapview) return;

            removePeilelinjal(mapview);
            removeAngleTool(mapview);
            closeMeasureControl();
        };
    }, []);

    useEffect(() => {
        peilelinjalDrawRef.current = peilelinjalDraw;
    }, [peilelinjalDraw]);

    useEffect(() => {
        angleDrawRef.current = angleDraw;
    }, [angleDraw]);

    useEffect(() => {
        prevMeasuresRef.current = prevMeasures;
    }, [prevMeasures]);

    useEffect(() => {
        const hasDistanceMeasure =
            measurementVM instanceof DistanceMeasurement2DViewModel ||
            prevMeasures.some((m) => m?.vm instanceof DistanceMeasurement2DViewModel);

        if (unit === Unit.Nautiskemil && hasDistanceMeasure) {
            startNauticalLabelNormalizationLoop(getMapView());
        } else {
            stopNauticalLabelNormalizationLoop();
        }

        return () => {
            if (unit !== Unit.Nautiskemil || !hasDistanceMeasure) {
                stopNauticalLabelNormalizationLoop();
            }
        };
    }, [unit, measurementVM, prevMeasures]);

    // Only the distance view model can be put into nautical miles. The area view model validates
    // against area units and silently keeps its current one for anything else, so it stays metric
    // and its perimeter is converted for display instead.
    const applyUnitToMeasurement = (
        measurement: DistanceMeasurement2DViewModel | AreaMeasurement2DViewModel | null | undefined,
        selectedUnit: string | null
    ) => {
        if (!(measurement instanceof DistanceMeasurement2DViewModel)) return;

        measurement.unit = selectedUnit === Unit.Nautiskemil ? "nautical-miles" : "meters";
    };

    // The area view model only accepts area units, so it cannot express the perimeter in nautical
    // miles itself. Its measurement carries the perimeter in metres regardless of the unit set on
    // it, so the perimeter is formatted from that number instead of from the label it produced.
    const getAreaMeasures = (areaVM: AreaMeasurement2DViewModel | null | undefined, selectedUnit: string | null) => {
        const measurementLabel: any = areaVM?.measurementLabel;
        const perimeterMeters = (areaVM?.measurement as any)?.perimeter;

        const perimeter =
            typeof perimeterMeters === "number"
                ? formatLength(perimeterMeters, selectedUnit)
                : measurementLabel?.perimeter;

        return [
            { key: "Areal", value: measurementLabel?.area },
            { key: "Omkrets", value: perimeter },
        ];
    };

    // Peilelinjal and the angle tool store their results as graphic attributes, so their measures
    // are derived back out of the label graphics instead of being kept in a second piece of state
    const getGraphicsMeasures = (graphics: Array<Graphic>, selectedUnit: string | null) => {
        const measures: Array<{ key: string; value: string }> = [];

        const lengthLabel = graphics.find((graphic) => graphic.attributes?.type === "lengthLabel");
        if (lengthLabel) {
            measures.push({ key: "Lengde", value: formatLength(lengthLabel.attributes.lengthMeters, selectedUnit) });
        }

        const angleLabel = graphics.find((graphic) => graphic.attributes?.type === "angleLabel");
        if (typeof angleLabel?.attributes?.angleDegrees === "number") {
            const { tool, angleDegrees, oppositeAngleDegrees, counterClockwiseAngleDegrees } = angleLabel.attributes;

            if (tool === "peilelinjal") {
                measures.push({ key: "Angle CW", value: `${angleDegrees.toFixed(1)}°` });
                measures.push({ key: "Angle CCW", value: `${counterClockwiseAngleDegrees.toFixed(1)}°` });
            } else {
                measures.push({ key: "Retning 1", value: `${angleDegrees.toFixed(1)}°` });
                measures.push({ key: "Retning 2", value: `${oppositeAngleDegrees.toFixed(1)}°` });
            }
        }

        return measures;
    };

    const getPrevMeasureMeasures = (prevMeasure: PrevMeasureEntry, selectedUnit: string | null) => {
        if (prevMeasure instanceof Array) {
            return getGraphicsMeasures(prevMeasure, selectedUnit);
        }

        const measurementLabel = prevMeasure?.vm?.measurementLabel;
        if (!measurementLabel) return [];

        if (prevMeasure.vm instanceof AreaMeasurement2DViewModel) {
            return getAreaMeasures(prevMeasure.vm, selectedUnit);
        }

        const value =
            typeof measurementLabel === "string" ? measurementLabel.replace(/\s*nmi$/i, " Nm") : measurementLabel;

        return [{ key: "Lengde", value }];
    };

    const normalizeNauticalLabelText = (text: string) => text.replaceAll(/\bnmi\b/gi, "Nm");

    const normalizeGraphicTextSymbol = (graphic: Graphic) => {
        const symbol = graphic?.symbol as TextSymbol | undefined;
        const symbolText = (symbol as any)?.text;

        if (typeof symbolText !== "string") return;
        if (!symbol || typeof (symbol as any).clone !== "function") return;

        const normalizedText = normalizeNauticalLabelText(symbolText);
        if (normalizedText === symbolText) return;

        const newSymbol: any = symbol.clone();
        newSymbol.text = normalizedText;
        graphic.symbol = newSymbol;
    };

    const normalizeNauticalDistanceLabelsInMap = (mapview: MapView | null | undefined) => {
        if (!mapview) return;

        mapview.graphics.forEach((graphic) => normalizeGraphicTextSymbol(graphic));

        mapview.map?.allLayers.forEach((layer: any) => {
            if (!layer?.graphics?.forEach) return;

            layer.graphics.forEach((graphic: Graphic) => normalizeGraphicTextSymbol(graphic));
        });
    };

    const stopNauticalLabelNormalizationLoop = () => {
        if (labelNormalizeIntervalRef.current) {
            globalThis.clearInterval(labelNormalizeIntervalRef.current);
            labelNormalizeIntervalRef.current = null;
        }
    };

    const startNauticalLabelNormalizationLoop = (mapview: MapView | null | undefined) => {
        if (!mapview) return;
        if (labelNormalizeIntervalRef.current) return;

        normalizeNauticalDistanceLabelsInMap(mapview);

        labelNormalizeIntervalRef.current = globalThis.setInterval(() => {
            normalizeNauticalDistanceLabelsInMap(mapview);
        }, 80);
    };

    const closeMeasureControl = () => {
        stopNauticalLabelNormalizationLoop();

        if (measurementVM) {
            measurementVM?.clear();
            setMeasurementVM(null);
            setDistanceActive(false);
            setAreaActive(false);
        }

        setPeilelinjalActive(false);
        setAngleToolActive(false);

        prevMeasuresRef.current?.forEach((m) => {
            m?.vm?.clear();
            m?.vm?.destroy();
        });
        setPrevMeasures([]);

        setCurrentMeasures([]);
        areaMeasurementVmRef.current = null;

        if (measurementVMRef.current) {
            measurementVMRef.current?.clear();
            measurementVMRef.current?.destroy();
            measurementVMRef.current = null;
        }
    };

    const watchMeasureState = (measurement: any) => {
        measurement.watch("state", (newValue: "disabled" | "ready" | "measuring" | "measured") => {
            if (newValue === "measured") {
                setDistanceActive(false);
                setAreaActive(false);
                setPeilelinjalActive(false);
                setAngleToolActive(false);

                // The functional form matters: this watcher is registered once, so the closure's
                // prevMeasures is a snapshot from when the measurement started
                setPrevMeasures((current) => appendMeasurement(current, measurement));
            }
        });
    };

    const watchMeasureResult = (measurement: DistanceMeasurement2DViewModel | AreaMeasurement2DViewModel) => {
        if (measurement instanceof DistanceMeasurement2DViewModel) {
            (measurement as Accessor).watch("measurementLabel", (newValue: any) => {
                if (!newValue) return;
                // Previous measurements recompute their label on a unit change, so the list has to re-derive
                setLabelVersion((version) => version + 1);
                const isActiveMeasurement = measurementVMRef.current === measurement;
                const isEditingMeasurement = measurement.state === "measuring";
                if (!isActiveMeasurement && !isEditingMeasurement) return;
                const formattedValue = typeof newValue === "string" ? newValue.replace(/\s*nmi$/i, " Nm") : newValue;
                setCurrentMeasures([{ key: "Lengde", value: formattedValue }]);
                normalizeNauticalDistanceLabelsInMap(measurement.view as MapView);
            });
            return;
        }

        if (measurement instanceof AreaMeasurement2DViewModel) {
            // this VM doesn't know it inherits Accessor...
            let mVM: any = measurement;
            mVM.watch("measurementLabel", (newValue: any) => {
                if (!newValue) return;
                setLabelVersion((version) => version + 1);
                const isActiveMeasurement = measurementVMRef.current === measurement;
                const isEditingMeasurement = measurement.state === "measuring";
                if (!isActiveMeasurement && !isEditingMeasurement) return;
                areaMeasurementVmRef.current = measurement;
                setCurrentMeasures(getAreaMeasures(measurement, unitRef.current));
            });
        }
    };

    const startDrawingPeilelinjal = (mapview: MapView) => {
        if (!measurementVM?.measurement || measurementVM?.state === "measuring") {
            measurementVM?.clear();
        }
        setMeasurementVM(null);

        const draw = new Draw({
            view: mapview,
        });
        setPeilelinjalDraw(draw);
        let action = draw.create("polyline", { mode: "click" });

        // fires when a vertex is added
        action.on("vertex-add", async function (evt) {
            await drawPeilelinjal(evt.vertices, setCurrentMeasures, mapview, unitRef.current);
            if (evt.vertices.length === 2) {
                draw.complete();
            }
        });

        // fires when the pointer moves
        action.on("cursor-update", async function (evt) {
            await drawPeilelinjal(evt.vertices, setCurrentMeasures, mapview, unitRef.current);
        });

        // fires when the drawing is completed
        action.on("draw-complete", async function (evt) {
            await drawPeilelinjal(evt.vertices, setCurrentMeasures, mapview, unitRef.current);
            const peilelinjalLayer = mapview?.map?.findLayerById("peilelinjal") as GraphicsLayer;
            if (!peilelinjalLayer) return;
            const peilelinjalGraphicsLayer = mapview.map?.findLayerById("peilelinjalGraphicsLayer") as GraphicsLayer;
            if (!peilelinjalGraphicsLayer) return;
            const peilelinjalGraphics = peilelinjalGraphicsLayer.graphics.toArray();
            peilelinjalLayer.addMany(peilelinjalGraphics);
            setPrevMeasures([...prevMeasures, peilelinjalGraphics]);
            peilelinjalGraphicsLayer.removeAll();

            setPeilelinjalActive(false);
        });
    };

    // The draw action type is decided up front, so the override is passed in explicitly instead of
    // read from state. Toggling it restarts the draw, which keeps the value valid for its lifetime.
    const startDrawingAngleMeasurement = (mapview: MapView, useSelectedStart = useSelectedFeatureAsStart) => {
        if (!measurementVM?.measurement || measurementVM?.state === "measuring") {
            measurementVM?.clear();
        }
        setMeasurementVM(null);

        const draw = new Draw({
            view: mapview,
        });
        setAngleDraw(draw);

        const fixedStartPoint = useSelectedStart && shownPoint ? [shownPoint.x, shownPoint.y] : null;

        let action: PolylineDrawAction;
        if (fixedStartPoint) {
            // A point action emits the same cursor-update/draw-complete events, it just never emits vertex-add
            action = draw.create("point", { mode: "click" }) as unknown as PolylineDrawAction;
        } else {
            action = draw.create("polyline", { mode: "click" });
        }

        const getAngleVertices = (evt: { vertices: number[][]; coordinates: number[] | null }) =>
            fixedStartPoint && evt.coordinates ? [fixedStartPoint, evt.coordinates] : evt.vertices;

        // fires when a vertex is added
        action.on("vertex-add", async function (evt) {
            await drawAngle(evt.vertices, setCurrentMeasures, mapview, unitRef.current);
            if (evt.vertices.length === 2) {
                draw.complete();
            }
        });

        // fires when the pointer moves
        action.on("cursor-update", async function (evt) {
            await drawAngle(getAngleVertices(evt), setCurrentMeasures, mapview, unitRef.current, fixedStartPoint);
        });

        // fires when the drawing is completed
        action.on("draw-complete", async function (evt) {
            await drawAngle(getAngleVertices(evt), setCurrentMeasures, mapview, unitRef.current, fixedStartPoint);
            const angleLayer = mapview?.map?.findLayerById("angleLayer") as GraphicsLayer;
            if (!angleLayer) return;
            const angleGraphicsLayer = mapview.map?.findLayerById("angleGraphicsLayer") as GraphicsLayer;
            if (!angleGraphicsLayer) return;
            const angleGraphics = angleGraphicsLayer.graphics.toArray();
            angleLayer.addMany(angleGraphics);
            setPrevMeasures([...prevMeasures, angleGraphics]);
            angleGraphicsLayer.removeAll();

            setAngleToolActive(false);
        });
    };

    const startDrawingLengthOrArea = (mapview: MapView, type: MeasureType.Distance | MeasureType.Area) => {
        peilelinjalDraw?.destroy();
        const peilelinjalGraphicsLayer = mapview.map?.findLayerById("peilelinjalGraphicsLayer") as GraphicsLayer;
        if (peilelinjalGraphicsLayer) {
            peilelinjalGraphicsLayer.removeAll();
        }
        setPeilelinjalDraw(null);
        peilelinjalDrawRef.current = null;

        angleDraw?.destroy();
        const angleGraphicsLayer = mapview.map?.findLayerById("angleGraphicsLayer") as GraphicsLayer;
        if (angleGraphicsLayer) {
            angleGraphicsLayer.removeAll();
        }
        setAngleDraw(null);
        angleDrawRef.current = null;

        // create measure viewmodel
        let measurement: DistanceMeasurement2DViewModel | AreaMeasurement2DViewModel | null = null;

        if (mapview instanceof MapView) {
            measurement =
                type === MeasureType.Distance ? new DistanceMeasurement2DViewModel() : new AreaMeasurement2DViewModel();
            measurement.view = mapview;
        }

        if (measurement) {
            applyUnitToMeasurement(measurement, unit);

            if (type === MeasureType.Distance) {
                // Temporary fix to disable snapping
                reactiveUtils.when(
                    //@ts-expect-error
                    () => measurement.tool,
                    (newValue: any) => {
                        newValue.snappingOptions.enabled = false;
                    }
                );
            }

            measurementVMRef.current = measurement;
            watchMeasureResult(measurement);
            watchMeasureState(measurement);

            measurement.start();
            setMeasurementVM(measurement);
        }
    };

    const toggleMeasurementTool = (type: MeasureType) => {
        const mapview = getMapView();
        if (!mapview) return;

        if (measurementVM?.state === "measuring") {
            if ((distanceActive && type === MeasureType.Distance) || (areaActive && type === MeasureType.Area)) {
                return;
            }
        }

        // finish current measure if any
        resetCurrentMeasure(false);

        // toggle button states
        setDistanceActive(type === MeasureType.Distance);
        setAreaActive(type === MeasureType.Area);
        setPeilelinjalActive(type === MeasureType.Peilelinjal);
        setAngleToolActive(type === MeasureType.Angle);

        if (type === MeasureType.Peilelinjal) {
            startDrawingPeilelinjal(mapview);
        } else if (type === MeasureType.Angle) {
            startDrawingAngleMeasurement(mapview);
        } else {
            startDrawingLengthOrArea(mapview, type);
        }
    };

    // The draw action type and start point are fixed at creation, so the in-progress angle has to be restarted
    const restartAngleMeasurement = (useSelectedStart: boolean) => {
        if (!angleToolActive) return;

        const mapview = getMapView();
        if (!mapview) return;

        angleDraw?.destroy();
        const angleGraphicsLayer = mapview.map?.findLayerById("angleGraphicsLayer") as GraphicsLayer;
        angleGraphicsLayer?.removeAll();
        setCurrentMeasures([]);

        startDrawingAngleMeasurement(mapview, useSelectedStart);
    };

    const toggleUseSelectedFeatureAsStart = (checked: boolean) => {
        setUseSelectedFeatureAsStart(checked);
        restartAngleMeasurement(checked);
    };

    // Pick up a new start point when another object is shown while the angle tool is active.
    // Keyed on the coordinates so showing a non-point object (or none) doesn't discard placed vertices.
    const shownPointKey = shownPoint ? `${shownPoint.x},${shownPoint.y}` : null;
    const prevShownPointKeyRef = useRef(shownPointKey);
    useEffect(() => {
        if (prevShownPointKeyRef.current === shownPointKey) return;
        prevShownPointKeyRef.current = shownPointKey;
        if (useSelectedFeatureAsStart) {
            restartAngleMeasurement(true);
        }
    }, [shownPointKey]);

    const angleLabelsOf = (graphics: Array<Graphic>) =>
        graphics.filter((graphic) => graphic?.attributes?.type !== "angleLine");

    /**
     * Rebuilds the labels of a finished angle after its geometry was dragged. The line graphic
     * itself is left alone: the sketch view model owns it while an update is running.
     *
     * Every drag frame starts a rebuild before the previous one has finished awaiting, so the
     * labels to remove are looked up by the measurement's id rather than remembered between
     * frames, and a frame that has been overtaken drops its result instead of applying it.
     */
    const rebuildEditedAngle = async (
        lineGraphic: Graphic,
        vertices: number[][],
        mapview: MapView,
        pinnedStart: number[] | null,
        isDone: boolean,
        sequence: number
    ) => {
        const angleLayer = mapview.map?.findLayerById("angleLayer") as GraphicsLayer;
        if (!angleLayer) return;

        const measurementId = lineGraphic.attributes?.measurementId;

        const { graphics } = await buildAngleGraphics(vertices, mapview, unitRef.current, pinnedStart, measurementId);

        // A later frame got here first, so this one is already out of date
        if (sequence !== angleEditSequenceRef.current) return;

        const rebuiltLabels = angleLabelsOf(graphics);
        if (rebuiltLabels.length === 0) return;

        const staleLabels = angleLayer.graphics
            .filter(
                (graphic) =>
                    graphic?.attributes?.measurementId === measurementId && graphic?.attributes?.type !== "angleLine"
            )
            .toArray();

        angleLayer.removeMany(staleLabels);
        angleLayer.addMany(rebuiltLabels);

        // Putting the pinned vertex back only on release, because rewriting the geometry on every
        // drag frame makes the sketch view model's own handles stutter
        if (isDone && pinnedStart) {
            lineGraphic.geometry = new Polyline({
                paths: [vertices],
                spatialReference: mapview.spatialReference,
            });
        }

        setPrevMeasures((current) => replaceMeasurementGraphics(current, lineGraphic, [lineGraphic, ...rebuiltLabels]));
    };

    const handleAngleSketchUpdate = async (event: UpdateEvent, mapview: MapView) => {
        const lineGraphic = event.graphics[0];

        // angleLayer holds the labels as well, and only the line is meant to be editable
        if (lineGraphic?.attributes?.type !== "angleLine") {
            angleSketchVMRef.current?.cancel();
            return;
        }

        const isDragging = event.toolEventInfo?.type === "reshape";
        const isDone = event.state === "complete" || event.toolEventInfo?.type === "reshape-stop";
        if (!isDragging && !isDone) return;

        const pinnedStart: number[] | null = lineGraphic.attributes?.pinnedStart ?? null;
        const path = (lineGraphic.geometry as Polyline)?.paths?.[0];
        if (!path || path.length < 2) return;

        const vertices = [isDone && pinnedStart ? pinnedStart : path[0], path[path.length - 1]];

        angleEditSequenceRef.current += 1;
        const sequence = angleEditSequenceRef.current;

        await rebuildEditedAngle(lineGraphic, vertices, mapview, pinnedStart, isDone, sequence);
    };

    const getAngleSketchVM = (mapview: MapView) => {
        if (angleSketchVMRef.current) return angleSketchVMRef.current;

        const angleLayer = mapview.map?.findLayerById("angleLayer") as GraphicsLayer;
        if (!angleLayer) return null;

        const sketchVM = new SketchViewModel({
            view: mapview,
            layer: angleLayer,
            updateOnGraphicClick: true,
            defaultUpdateOptions: {
                tool: "reshape",
                enableRotation: false,
                enableScaling: false,
                toggleToolOnClick: false,
                // "none" for the edges keeps the midpoint handles away, so the two vertices an
                // angle is made of cannot be added to
                reshapeOptions: {
                    edgeOperation: "none",
                    shapeOperation: "none",
                    vertexOperation: "move-xy",
                },
            },
            snappingOptions: { enabled: false },
        });

        sketchVM.on("update", (event) => {
            void handleAngleSketchUpdate(event, mapview);
        });

        angleSketchVMRef.current = sketchVM;
        return sketchVM;
    };

    const cancelAngleEdit = () => {
        angleSketchVMRef.current?.cancel();
        // Invalidates any rebuild still in flight, so it cannot land after the edit was dropped
        angleEditSequenceRef.current += 1;
    };

    /**
     * Editing is switched off while a measurement is being drawn, so a click meant to place a
     * vertex cannot grab a nearby angle instead.
     */
    const setAngleEditingEnabled = (enabled: boolean, mapview?: MapView) => {
        cancelAngleEdit();

        if (!enabled) {
            if (angleSketchVMRef.current) angleSketchVMRef.current.updateOnGraphicClick = false;
            return;
        }

        if (!mapview) return;

        const angleLayer = mapview.map?.findLayerById("angleLayer") as GraphicsLayer;
        // Nothing has been drawn yet, so there is nothing to make editable
        if (!angleSketchVMRef.current && angleLayer?.graphics?.length === 0) return;

        const sketchVM = getAngleSketchVM(mapview);
        if (sketchVM) sketchVM.updateOnGraphicClick = true;
    };

    // Editing is available whenever nothing is being drawn. Deriving it from the tool states is
    // what keeps it from staying off after a measurement made with one of the other tools.
    useEffect(() => {
        const isDrawing = distanceActive || areaActive || peilelinjalActive || angleToolActive;

        setAngleEditingEnabled(!isDrawing, getMapView() ?? undefined);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [distanceActive, areaActive, peilelinjalActive, angleToolActive]);

    const removeAngleTool = (mapview: MapView) => {
        angleSketchVMRef.current?.cancel();
        angleSketchVMRef.current?.destroy();
        angleSketchVMRef.current = null;
        angleEditSequenceRef.current += 1;

        angleDraw?.destroy();
        angleDrawRef.current?.destroy();
        setAngleDraw(null);

        const angleLayer = mapview.map?.findLayerById("angleLayer") as GraphicsLayer;
        if (!angleLayer) return;
        const angleGraphicsLayer = mapview.map?.findLayerById("angleGraphicsLayer") as GraphicsLayer;
        if (!angleGraphicsLayer) return;

        angleGraphicsLayer.removeAll();
        angleLayer.removeAll();
    };

    const removePeilelinjal = (mapview: MapView) => {
        peilelinjalDraw?.destroy();
        peilelinjalDrawRef.current?.destroy();
        setPeilelinjalDraw(null);

        const peilelinjalLayer = mapview.map?.findLayerById("peilelinjal") as GraphicsLayer;
        if (!peilelinjalLayer) return;
        const peilelinjalGraphicsLayer = mapview.map?.findLayerById("peilelinjalGraphicsLayer") as GraphicsLayer;
        if (!peilelinjalGraphicsLayer) return;

        peilelinjalGraphicsLayer.removeAll();
        peilelinjalLayer.removeAll();
    };

    const resetCurrentMeasure = (restart: boolean) => {
        const mapview = getMapView();
        if (!mapview) return;

        stopNauticalLabelNormalizationLoop();
        cancelAngleEdit();

        setAreaActive(false);
        setDistanceActive(false);
        setPeilelinjalActive(false);
        setAngleToolActive(false);
        setCurrentMeasures([]);
        areaMeasurementVmRef.current = null;

        if (restart) {
            prevMeasures.forEach((m) => {
                m?.vm?.clear();
                m?.vm?.destroy();
            });

            removePeilelinjal(mapview);
            removeAngleTool(mapview);
            setPrevMeasures([]);
        }
    };

    const undoPrevMeasure = () => {
        const mapview = getMapView();
        if (!mapview) return;
        if (prevMeasures.length === 0) return;

        cancelAngleEdit();

        const last = prevMeasures[prevMeasures.length - 1];
        setPrevMeasures(prevMeasures.slice(0, -1));
        setCurrentMeasures([]);
        if (last instanceof Array) {
            const peilelinjalLayer = mapview.map?.findLayerById("peilelinjal") as GraphicsLayer;
            if (peilelinjalLayer) {
                peilelinjalLayer.graphics.filter((g) => last.includes(g)).forEach((g) => peilelinjalLayer.remove(g));
            }
            const angleLayer = mapview.map?.findLayerById("angleLayer") as GraphicsLayer;
            if (angleLayer) {
                angleLayer.graphics.filter((g) => last.includes(g)).forEach((g) => angleLayer.remove(g));
            }
        } else {
            last?.vm?.clear();
        }
    };

    const closeTool = () => {
        const mapview = getMapView();
        if (!mapview) return;

        removePeilelinjal(mapview);
        removeAngleTool(mapview);
        setPeilelinjalActive(false);
        setAngleToolActive(false);
        dispatch(setMeasureToolOpen(false));
        resetCurrentMeasure(true);
    };

    useEffect(() => {
        applyUnitToMeasurement(measurementVM, unit);
        prevMeasures.forEach((m) => applyUnitToMeasurement(m?.vm, unit));

        const mapview = getMapView();
        if (!mapview?.map) return;

        // Both tools draw length labels, so both layers have to be reformatted on a unit change
        const graphicLayers = ["peilelinjal", "angleLayer"]
            .map((layerId) => mapview.map?.findLayerById(layerId) as GraphicsLayer)
            .filter(Boolean);

        const graphicsToAdd: Array<Graphic> = [];

        graphicLayers.forEach((layer) => {
            const layerGraphicsToAdd: Array<Graphic> = [];
            const layerGraphicsToRemove: Array<Graphic> = [];

            layer.graphics.forEach((graphic) => {
                if (graphic.attributes?.type !== "lengthLabel") return;

                const newGraphic = graphic.clone();
                (newGraphic.symbol as TextSymbol).text = formatLength(graphic.attributes.lengthMeters, unit);

                layerGraphicsToAdd.push(newGraphic);
                layerGraphicsToRemove.push(graphic);

                prevMeasures.forEach((m) => {
                    if (m instanceof Array && m.includes(graphic)) {
                        const index = m.indexOf(graphic);
                        m[index] = newGraphic;
                    }
                });
            });

            layer.addMany(layerGraphicsToAdd);
            layer.removeMany(layerGraphicsToRemove);
            graphicsToAdd.push(...layerGraphicsToAdd);
        });

        if (!measurementVM && prevMeasures.length > 0 && graphicsToAdd.length > 0) {
            const newCurrentMeasures = [...currentMeasures];
            let lengthUpdated = false;

            for (const element of newCurrentMeasures) {
                if (element.key === "Lengde") {
                    element.value = (graphicsToAdd[graphicsToAdd.length - 1].symbol as TextSymbol).text;
                    lengthUpdated = true;
                    break;
                }
            }

            if (lengthUpdated) {
                setCurrentMeasures(newCurrentMeasures);
            }
        }

        const isShowingAreaMeasure = currentMeasures.some((measure) => measure.key === "Omkrets");

        if (isShowingAreaMeasure && areaMeasurementVmRef.current) {
            setCurrentMeasures(getAreaMeasures(areaMeasurementVmRef.current, unit));
        } else if (!measurementVM && isShowingAreaMeasure) {
            const previousAreaMeasure = [...prevMeasures]
                .reverse()
                .find((m) => m?.vm instanceof AreaMeasurement2DViewModel);

            if (previousAreaMeasure?.vm) {
                setCurrentMeasures(getAreaMeasures(previousAreaMeasure.vm, unit));
            }
        }
    }, [unit]);

    const measureListItems = useMemo(() => {
        const items = prevMeasures.map((prevMeasure, index) => ({
            number: index + 1,
            active: false,
            measures: getPrevMeasureMeasures(prevMeasure, unit),
        }));

        // The in-progress measurement takes the next number until it lands in prevMeasures
        const isMeasuring = distanceActive || areaActive || peilelinjalActive || angleToolActive;
        if (isMeasuring && currentMeasures.length > 0) {
            items.push({ number: prevMeasures.length + 1, active: true, measures: currentMeasures });
        }

        return items;
        // labelVersion is a dependency so the list re-derives when a view model recomputes its label
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [
        prevMeasures,
        currentMeasures,
        unit,
        labelVersion,
        distanceActive,
        areaActive,
        peilelinjalActive,
        angleToolActive,
    ]);

    useEffect(() => {
        activeMeasureItemRef.current?.scrollIntoView({ block: "nearest" });
    }, [measureListItems.length]);

    const calcHeightAndWidth = () => {
        const heightAndWidth = { x: 0, y: 0 };

        heightAndWidth.x = Math.max(window.innerWidth - 600, 200);
        heightAndWidth.y = 40;

        return heightAndWidth;
    };

    return (
        <FloatingWindow
            toolRef={measureToolRef}
            closeWindow={closeTool}
            title="Måleverktøy"
            Icon={RulerIcon}
            width={500}
            defaultPosition={calcHeightAndWidth()}
        >
            <div className="measurement-tools">
                <NfsButton
                    selected={distanceActive ?? false}
                    outlined
                    onClick={() => toggleMeasurementTool(MeasureType.Distance)}
                >
                    <LineIcon />
                    Linje
                </NfsButton>
                <NfsButton
                    selected={areaActive ?? false}
                    outlined
                    onClick={() => toggleMeasurementTool(MeasureType.Area)}
                >
                    <AreaIcon />
                    Areal
                </NfsButton>
                {PEILELINJAL_ENABLED_FLAG && (
                    <NfsButton
                        selected={peilelinjalActive ?? false}
                        outlined
                        onClick={() => toggleMeasurementTool(MeasureType.Peilelinjal)}
                    >
                        <PeilelinjalIcon />
                        Peilelinjal
                    </NfsButton>
                )}
                <NfsButton
                    selected={angleToolActive ?? false}
                    outlined
                    onClick={() => toggleMeasurementTool(MeasureType.Angle)}
                >
                    <PeilelinjalIcon />
                    Vinkelmåler
                </NfsButton>
            </div>
            <div className="measurement-content">
                {!distanceActive && !areaActive && !peilelinjalActive && !angleToolActive && (
                    <span className="no-measurement">Velg målemetode</span>
                )}
                {angleToolActive && (
                    <div className="angle-start-option">
                        <span className="flex-row center small-gap">
                            <Checkbox
                                id="use-selected-feature-start"
                                checked={useSelectedFeatureAsStart && hasShownPointFeature}
                                onChange={toggleUseSelectedFeatureAsStart}
                                disabled={!hasShownPointFeature}
                            />
                            <label htmlFor="use-selected-feature-start" className="small-paragraph">
                                Bruk valgt objekt som startpunkt
                            </label>
                        </span>
                        {!hasShownPointFeature && (
                            <span className="angle-start-hint">
                                Åpne ett objekt i visningspanelet for å kunne bruke det som startpunkt.
                            </span>
                        )}
                    </div>
                )}
                {(distanceActive || areaActive || peilelinjalActive || angleToolActive) && (
                    <span className="measurement-instructions">
                        {distanceActive || areaActive
                            ? "Klikk for å sette målepunkter i kartet. Dobbeltklikk for å avslutte måling."
                            : angleToolActive && useSelectedFeatureAsStart && hasShownPointFeature
                              ? "Klikk for å sette endepunkt."
                              : "Klikk for å sette startpunkt. Klikk igjen for å sette endepunkt."}
                    </span>
                )}
                {/* {prevMeasures.length > 0 && ( */}
                {/* <> */}
                <div className="measurement-result">
                    {measureListItems.length > 0 && (
                        <ol className="measurement-list">
                            {measureListItems.map((item) => {
                                return (
                                    <li
                                        key={item.number}
                                        ref={item.active ? activeMeasureItemRef : null}
                                        className={item.active ? "active" : ""}
                                    >
                                        <span className="measurement-list-number small-paragraph-bold">
                                            {item.number}.
                                        </span>
                                        <span className="measurement-list-values">
                                            {item.measures.map((kvp) => {
                                                return (
                                                    <div key={kvp.key}>
                                                        <span className="small-paragraph">{kvp.key}: </span>
                                                        <span className="small-paragraph-bold">{kvp.value}</span>
                                                    </div>
                                                );
                                            })}
                                        </span>
                                    </li>
                                );
                            })}
                        </ol>
                    )}
                    <div className="flex-row center small-gap" style={{ width: "200px" }}>
                        <span className="small-paragraph">Enhet:</span>
                        <NfsDropdown
                            options={[Unit.Meter, Unit.Nautiskemil]}
                            selectedOption={unit}
                            setSelectedOption={setUnit}
                            placeholder=""
                        />
                    </div>
                </div>
                <div className="flex-row space-between">
                    {/* <span>
                                    <input type="checkbox" checked={showMoh} onChange={() => setShowMoh(!showMoh)} id="moh" name="moh" />
                                    <label htmlFor="moh">Vis moh</label>
                                    </span> */}
                    <span></span>
                    <span className="flex-row small-gap">
                        <NfsButton
                            className="empty inverted"
                            onClick={() => undoPrevMeasure()}
                            disabled={prevMeasures.length < 1}
                        >
                            <PreviousIcon />
                        </NfsButton>
                        <NfsButton className="empty inverted" onClick={() => resetCurrentMeasure(true)}>
                            <ResetIcon />
                        </NfsButton>
                    </span>
                </div>
                {/* </> */}
                {/* )} */}
            </div>
        </FloatingWindow>
    );
};

export default MeasurementControl;
